/**
 * EditProfile — Full profile editor.
 * Route: /profile/edit
 *
 * Matches mobile app's EditProfileScreen capabilities:
 *   Profile photo, handle, location, about me, looking for,
 *   interests, personal info (name, DOB, gender, orientation),
 *   privacy toggles (show age, show orientation).
 *
 * Uses direct Firestore writes with debounced auto-save (same as mobile).
 * Handle ops go through Cloud Functions.
 * Image upload goes to Firebase Storage + AI moderation.
 */

import React, { useEffect, useState, useCallback, useRef } from 'react';
import { ArrowLeft, Check, Camera, Loader } from 'react-feather';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useAuthGate } from '../hooks/useAuthGate';
import SEO from '../components/SEO';
import {
  getMyProfile,
  saveProfile,
  uploadProfileImage,
  validateHandle,
  checkHandleAvailability,
  updateHandle,
  checkHandleRestriction,
} from '../services/accountService';
import {
  GENDER_OPTIONS,
  ORIENTATION_OPTIONS,
  LOOKING_FOR_OPTIONS,
  INTERESTED_IN_OPTIONS,
} from '../types/account';
import type { EditableProfile, Gender, SexualOrientation, LookingFor, InterestedIn } from '../types/account';
import { capture } from '../services/posthog';
import { clearHomeFeedCache, markHomeFeedStale } from '../components/Home/homeFeedCache';
import { normalizeInterests, serializeInterestsForWrite } from '../utils/interests';
import '../styles/modern.css';
import '../styles/account.css';

// Country list (subset — the app has 195; using common ones + option to type)
const COUNTRIES = [
  'Afghanistan','Albania','Algeria','Argentina','Australia','Austria','Bangladesh',
  'Belgium','Brazil','Canada','Chile','China','Colombia','Czech Republic','Denmark',
  'Egypt','Finland','France','Germany','Ghana','Greece','India','Indonesia','Ireland',
  'Israel','Italy','Jamaica','Japan','Kenya','Malaysia','Mexico','Morocco','Netherlands',
  'New Zealand','Nigeria','Norway','Pakistan','Peru','Philippines','Poland','Portugal',
  'Romania','Russia','Saudi Arabia','Singapore','South Africa','South Korea','Spain',
  'Sri Lanka','Sweden','Switzerland','Taiwan','Thailand','Turkey','Ukraine',
  'United Arab Emirates','United Kingdom','United States','Vietnam','Zimbabwe',
];

export default function EditProfile() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isAuthenticated, redirectToLogin } = useAuthGate();
  const uid = user?.uid;

  // ─── Form state ─────────────────────────────────────────────────────
  const [profile, setProfile] = useState<Partial<EditableProfile>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Image
  const [imageUploading, setImageUploading] = useState(false);
  const [imageError, setImageError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Handle editing
  const [editingHandle, setEditingHandle] = useState(false);
  const [handleInput, setHandleInput] = useState('');
  const [handleStatus, setHandleStatus] = useState<string | null>(null);
  const [handleSaving, setHandleSaving] = useState(false);
  const [handleRestricted, setHandleRestricted] = useState(false);

  // Interest tags
  const [tagInput, setTagInput] = useState('');

  // ─── Auto-save debounce ─────────────────────────────────────────────
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingRef = useRef<Partial<EditableProfile> | null>(null);

  /**
   * The home desk's stage is computed from this doc — an interest, an about
   * line, a lookingFor or a photo now leaves S0 (PPL-488) — but the desk
   * caches its feed for five minutes and throttles its focus refetch, so
   * without this the user walks back to /f8chat and is handed the same S0
   * two-path desk they just left. Drop the cached copy AND flag it, so the
   * next mount waits for the server rather than painting what it has.
   */
  const invalidateHomeDesk = useCallback((who: string) => {
    clearHomeFeedCache(who);
    markHomeFeedStale(who);
  }, []);

  const debouncedSave = useCallback(
    (fields: Partial<EditableProfile>) => {
      if (!uid) return;
      pendingRef.current = { ...(pendingRef.current || {}), ...fields };
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
      saveTimerRef.current = setTimeout(async () => {
        if (!pendingRef.current) return;
        setSaving(true);
        try {
          await saveProfile(uid, pendingRef.current);
          pendingRef.current = null;
          invalidateHomeDesk(uid);
          setSaved(true);
          setTimeout(() => setSaved(false), 2000);
        } catch (err: any) {
          setError(err?.message || 'Failed to save');
        } finally {
          setSaving(false);
        }
      }, 1000);
    },
    [uid, invalidateHomeDesk],
  );

  // Cleanup
  useEffect(() => {
    return () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    };
  }, []);

  // ─── Auth gate ──────────────────────────────────────────────────────
  useEffect(() => {
    if (!isAuthenticated && !loading) {
      redirectToLogin();
    }
  }, [isAuthenticated, loading, redirectToLogin]);

  // ─── Load profile ─────────────────────────────────────────────────
  useEffect(() => {
    if (!uid) return;
    (async () => {
      setLoading(true);
      try {
        const data = await getMyProfile(uid);
        setProfile(data);
      } catch (err: any) {
        setError(err?.message || 'Failed to load profile');
      } finally {
        setLoading(false);
      }
    })();
  }, [uid]);

  // ─── Field updater ─────────────────────────────────────────────────
  const updateField = (key: keyof EditableProfile, value: any) => {
    setProfile((prev) => ({ ...prev, [key]: value }));
    debouncedSave({ [key]: value });
  };

  // ─── Image upload ─────────────────────────────────────────────────
  const handleImageSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !uid) return;

    setImageUploading(true);
    setImageError(null);
    try {
      const result = await uploadProfileImage(uid, file);
      if (!result.isValid) {
        setImageError(result.reason || 'Image was rejected by moderation');
        capture('profile_photo_rejected', {
          source: 'file',
          reason: result.reason || 'moderation_rejected',
          platform: 'web',
        });
      } else {
        setProfile((prev) => ({ ...prev, profileImageUrl: result.url }));
        // A photo is one of the four things that leave S0, and it is written
        // outside the autosave.
        invalidateHomeDesk(uid);
        capture('profile_photo_added', { source: 'file', platform: 'web' });
      }
    } catch (err: any) {
      setImageError(err?.message || 'Upload failed');
    } finally {
      setImageUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  // ─── Handle editing ─────────────────────────────────────────────────
  const startEditHandle = async () => {
    try {
      const restriction = await checkHandleRestriction();
      if (!restriction.canUpdate) {
        setHandleRestricted(true);
        setHandleStatus(
          `You can change your handle after ${restriction.nextAllowedDate || '3 months from last change'}.`,
        );
        return;
      }
    } catch {
      // Allow editing — restriction check may not exist yet
    }
    setHandleInput(profile.tetherHandle || '');
    setEditingHandle(true);
    setHandleStatus(null);
    setHandleRestricted(false);
  };

  const checkHandle = async () => {
    if (!handleInput || handleInput.length < 8) {
      setHandleStatus('Handle must be at least 8 characters');
      return;
    }

    setHandleStatus('Checking…');
    try {
      const valid = await validateHandle(handleInput);
      if (!valid.isValid) {
        setHandleStatus(valid.reason || 'Invalid handle');
        return;
      }
      const available = await checkHandleAvailability(handleInput);
      if (!available.isAvailable) {
        setHandleStatus('This handle is already taken');
        return;
      }
      setHandleStatus('✓ Available');
    } catch (err: any) {
      setHandleStatus(err?.message || 'Check failed');
    }
  };

  const saveHandle = async () => {
    if (!handleInput || handleStatus !== '✓ Available') return;
    setHandleSaving(true);
    try {
      const result = await updateHandle(handleInput);
      if (result.success) {
        setProfile((prev) => ({ ...prev, tetherHandle: handleInput }));
        setEditingHandle(false);
        setHandleStatus(null);
      } else {
        setHandleStatus(result.message || 'Failed to update handle');
      }
    } catch (err: any) {
      setHandleStatus(err?.message || 'Failed to update handle');
    } finally {
      setHandleSaving(false);
    }
  };

  // ─── Deep link at the interests editor ─────────────────────────────
  // `/profile/edit#interests` is where the find pane's "get matched" card
  // sends a thin profile (PPL-486). The section only exists once the profile
  // has loaded, so the browser's own hash jump has nothing to land on — scroll
  // once loading is done instead.
  useEffect(() => {
    if (loading || window.location.hash !== '#interests') return;
    const section = document.getElementById('interests');
    if (!section) return;
    section.scrollIntoView({ behavior: 'smooth', block: 'center' });
    section.querySelector('input')?.focus({ preventScroll: true });
  }, [loading]);

  // ─── Interest tags ─────────────────────────────────────────────────
  // Hydrate from EITHER shape — the canonical array, or the comma-separated
  // string older builds wrote — and always write the array back (PPL-490).
  const interests = normalizeInterests(profile.interests);

  const addTag = () => {
    // The raw input goes through the normaliser's STRING branch first, so a
    // typed "hiking, jazz" becomes two chips instead of one comma-bearing tag
    // that no array-contains query could ever match.
    const updated = normalizeInterests([...interests, ...normalizeInterests(tagInput)]);
    setTagInput('');
    if (updated.length === interests.length) return; // blank or already there
    updateField('interests', serializeInterestsForWrite(updated));
  };

  const removeTag = (idx: number) => {
    updateField('interests', serializeInterestsForWrite(interests.filter((_, i) => i !== idx)));
  };

  const handleTagKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addTag();
    }
  };

  // ─── Render ─────────────────────────────────────────────────────────

  if (!isAuthenticated) return null;

  return (
    <div className="page-shell">
      <div className="page-shell__inner page-shell__inner--narrow">
        <SEO title="Edit Profile | pplkpr" description="Edit your pplkpr profile" path="/profile/edit" />

        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <button className="up-back" onClick={() => navigate(-1)}>
            <ArrowLeft size={14} />
            <span>back</span>
          </button>
          <div className="edit-profile-save-status">
            {saving && <span className="save-indicator saving">saving...</span>}
            {saved && <span className="save-indicator saved"><Check size={14} /> saved</span>}
          </div>
        </div>

        {error && (
          <div className="glass-panel" style={{ padding: 12, marginBottom: 16, borderColor: 'rgba(255,107,107,0.3)', color: '#ff6b6b', fontSize: 13 }}>
            {error}
          </div>
        )}

        {loading && (
          <div className="glass-panel" style={{ padding: 40, textAlign: 'center' }}>
            <div style={{ fontSize: 14, color: 'var(--text-muted)' }}>loading profile...</div>
          </div>
        )}

        {!loading && (
          <>
            <h1 className="page-header__title" style={{ marginBottom: 24 }}>edit profile</h1>

            {/* ── Profile Photo ───────────────────────── */}
            <div className="glass-panel" style={{ padding: 24, marginBottom: 16 }}>
              <div className="ep-section-label">profile photo</div>
              <div className="edit-profile-photo-section" style={{ borderBottom: 'none', marginBottom: 0, paddingBottom: 0 }}>
                <div className="edit-profile-photo" onClick={() => fileInputRef.current?.click()}>
                  {profile.profileImageUrl ? (
                    <img src={profile.profileImageUrl} alt="Profile" />
                  ) : (
                    <div className="edit-profile-photo-placeholder">
                      <Camera size={24} />
                    </div>
                  )}
                  {imageUploading && (
                    <div className="edit-profile-photo-overlay">
                      <Loader size={20} className="spin" />
                    </div>
                  )}
                </div>
                <button
                  className="primary-button"
                  style={{ padding: '8px 16px', fontSize: 12 }}
                  onClick={() => fileInputRef.current?.click()}
                  disabled={imageUploading}
                >
                  {imageUploading ? 'uploading...' : 'change photo'}
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  style={{ display: 'none' }}
                  onChange={handleImageSelect}
                />
                {imageError && <span className="edit-profile-field-error">{imageError}</span>}
                <span className="ep-hint">square image, max 5 MB</span>
              </div>
            </div>

            {/* ── Handle ──────────────────────────────── */}
            <div className="glass-panel" style={{ padding: 20, marginBottom: 16 }}>
              <div className="ep-section-label">handle</div>
              {!editingHandle ? (
                <div className="edit-profile-handle-display">
                  <span className="edit-profile-handle-value">
                    @{profile.tetherHandle || '—'}
                  </span>
                  <button className="ep-ghost-btn" onClick={startEditHandle}>edit</button>
                </div>
              ) : (
                <div className="edit-profile-handle-edit">
                  <div className="edit-profile-handle-row">
                    <span>@</span>
                    <input
                      type="text"
                      className="ep-input"
                      value={handleInput}
                      onChange={(e) => {
                        setHandleInput(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ''));
                        setHandleStatus(null);
                      }}
                      maxLength={20}
                      placeholder="8-20 chars, a-z 0-9 _"
                    />
                    <button className="ep-ghost-btn" onClick={checkHandle}>check</button>
                  </div>
                  {handleStatus && (
                    <span className={`edit-profile-handle-status ${handleStatus.startsWith('✓') ? 'ok' : 'warn'}`}>
                      {handleStatus}
                    </span>
                  )}
                  <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                    <button className="ep-ghost-btn" onClick={() => setEditingHandle(false)}>cancel</button>
                    <button
                      className="primary-button"
                      style={{ padding: '7px 14px', fontSize: 12 }}
                      onClick={saveHandle}
                      disabled={handleSaving || handleStatus !== '✓ Available'}
                    >
                      {handleSaving ? 'saving...' : 'save handle'}
                    </button>
                  </div>
                </div>
              )}
              {handleRestricted && handleStatus && (
                <span className="edit-profile-field-error" style={{ marginTop: 8, display: 'block' }}>{handleStatus}</span>
              )}
            </div>

            {/* ── Personal Information ────────────────── */}
            <div className="glass-panel" style={{ padding: 20, marginBottom: 16 }}>
              <div className="ep-section-label">personal information</div>

              <div className="ep-field">
                <label className="ep-field-label">first name</label>
                <input
                  type="text"
                  className="ep-input"
                  value={profile.firstName || ''}
                  onChange={(e) => updateField('firstName', e.target.value.slice(0, 30))}
                  maxLength={30}
                  placeholder="your first name"
                />
              </div>

              <div className="ep-field">
                <label className="ep-field-label">last name</label>
                <input
                  type="text"
                  className="ep-input"
                  value={profile.lastName || ''}
                  onChange={(e) => updateField('lastName', e.target.value)}
                  placeholder="your last name"
                />
              </div>

              <div className="ep-field">
                <label className="ep-field-label">date of birth</label>
                <input
                  type="date"
                  className="ep-input"
                  value={profile.dateOfBirth || ''}
                  onChange={(e) => updateField('dateOfBirth', e.target.value)}
                  max={new Date().toISOString().split('T')[0]}
                />
              </div>

              <div className="ep-field">
                <label className="ep-field-label">gender</label>
                <select
                  className="ep-input"
                  value={profile.gender || ''}
                  onChange={(e) => updateField('gender', e.target.value as Gender)}
                >
                  <option value="">select...</option>
                  {GENDER_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>

              <div className="ep-field">
                <label className="ep-field-label">sexual orientation</label>
                <select
                  className="ep-input"
                  value={profile.sexualOrientation || ''}
                  onChange={(e) => updateField('sexualOrientation', e.target.value as SexualOrientation)}
                >
                  <option value="">select...</option>
                  {ORIENTATION_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* ── Privacy Toggles ─────────────────────── */}
            <div className="glass-panel" style={{ padding: 20, marginBottom: 16 }}>
              <div className="ep-section-label">privacy</div>

              <div className="edit-profile-toggle-row">
                <span>show age on profile</span>
                <label className="toggle-switch">
                  <input
                    type="checkbox"
                    checked={profile.showAge !== false}
                    onChange={(e) => updateField('showAge', e.target.checked)}
                  />
                  <span className="toggle-slider" />
                </label>
              </div>

              <div className="edit-profile-toggle-row">
                <span>show sexual orientation on profile</span>
                <label className="toggle-switch">
                  <input
                    type="checkbox"
                    checked={profile.showSexualOrientation !== false}
                    onChange={(e) => updateField('showSexualOrientation', e.target.checked)}
                  />
                  <span className="toggle-slider" />
                </label>
              </div>
            </div>

            {/* ── Location ────────────────────────────── */}
            <div className="glass-panel" style={{ padding: 20, marginBottom: 16 }}>
              <div className="ep-section-label">location</div>

              <div className="ep-field">
                <label className="ep-field-label">country</label>
                <select
                  className="ep-input"
                  value={profile.country || ''}
                  onChange={(e) => updateField('country', e.target.value)}
                >
                  <option value="">select country...</option>
                  {COUNTRIES.map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </div>

              <div className="ep-field">
                <label className="ep-field-label">city</label>
                <input
                  type="text"
                  className="ep-input"
                  value={profile.city || ''}
                  onChange={(e) => updateField('city', e.target.value)}
                  placeholder="your city"
                />
              </div>
            </div>

            {/* ── About & Interests ──────────────────── */}
            <div className="glass-panel" style={{ padding: 20, marginBottom: 16 }}>
              <div className="ep-section-label">about you</div>

              <div className="ep-field">
                <label className="ep-field-label">about me</label>
                <textarea
                  className="ep-input ep-textarea"
                  value={profile.aboutMe || ''}
                  onChange={(e) => updateField('aboutMe', e.target.value.slice(0, 500))}
                  maxLength={500}
                  rows={4}
                  placeholder="tell people about yourself..."
                />
                <span className="ep-hint">{(profile.aboutMe || '').length}/500</span>
              </div>

              <div className="ep-field">
                <label className="ep-field-label">looking for</label>
                <select
                  className="ep-input"
                  value={profile.lookingFor || ''}
                  onChange={(e) => updateField('lookingFor', e.target.value as LookingFor)}
                >
                  <option value="">select...</option>
                  {LOOKING_FOR_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>

              <div className="ep-field">
                <label className="ep-field-label">interested in</label>
                <select
                  className="ep-input"
                  value={profile.interestedIn || ''}
                  onChange={(e) => updateField('interestedIn', e.target.value as InterestedIn)}
                >
                  <option value="">select...</option>
                  {INTERESTED_IN_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>

              <div className="ep-field" id="interests">
                <label className="ep-field-label">interests</label>
                <div className="ep-tags-wrap">
                  {interests.map((t, i) => (
                    <span key={t} className="tag-pill tag-pill--violet" style={{ gap: 4 }}>
                      {t}
                      <button
                        type="button"
                        onClick={() => removeTag(i)}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: 'inherit',
                          cursor: 'pointer',
                          padding: 0,
                          fontSize: 14,
                          lineHeight: 1,
                          opacity: 0.7,
                        }}
                      >
                        x
                      </button>
                    </span>
                  ))}
                  <input
                    type="text"
                    className="ep-tag-input"
                    value={tagInput}
                    onChange={(e) => setTagInput(e.target.value)}
                    onKeyDown={handleTagKeyDown}
                    onBlur={addTag}
                    placeholder="type an interest and press enter"
                  />
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
